import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { AuthContext } from '../context/AuthContext';
import Spinner from '../components/Spinner';
import SEO from '../components/SEO';
import ShareButton from '../components/ShareButton';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
    ClockIcon, 
    AcademicCapIcon, 
    CheckCircleIcon, 
    BookOpenIcon,
    TrophyIcon,
    PlayIcon,
    ArrowRightIcon,
    ChartBarIcon
} from '@heroicons/react/24/outline';

const CourseDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, token } = useContext(AuthContext);
    
    // SSR'dan gelen initial data'yı kullan
    const initialData = typeof window !== 'undefined' ? window.__INITIAL_DATA__ : null;
    const [course, setCourse] = useState(initialData?.course || null);
    const [enrollment, setEnrollment] = useState(null);
    const [loading, setLoading] = useState(!initialData?.course && !initialData?.notFound);
    const [isEnrolled, setIsEnrolled] = useState(false);

    const fetchCourseAndEnrollment = useCallback(async () => {
        // SSR'dan notFound geldiyse veya course varsa, fetch etme
        if (initialData?.notFound || initialData?.course) {
            // Course data varsa id doğrulaması yap (farklı kursa navigate edilmiş olabilir)
            if (initialData?.course) {
                const slugMatch = initialData.course.slug === id;
                const idMatch = initialData.course._id?.toString() === id;
                if (!slugMatch && !idMatch) {
                    // Farklı kursa ait data — fall through to fetch
                } else {
                    if (typeof window !== 'undefined') window.__INITIAL_DATA__ = null;
                    setLoading(false);
                    return;
                }
            } else {
                // notFound case
                if (typeof window !== 'undefined') window.__INITIAL_DATA__ = null;
                setLoading(false);
                return;
            }
        }
        
        setLoading(true);
        setIsEnrolled(false);
        setEnrollment(null);
        try {
            const config = token ? { headers: { 'x-auth-token': token } } : {};
            const courseRes = await axios.get(`${API_BASE_URL}/api/courses/${id}`, config);
            setCourse(courseRes.data);

            if (token) {
                try {
                     const enrollmentRes = await axios.get(`${API_BASE_URL}/api/enrollments/status/${id}`, config);
                     if (enrollmentRes.data) {
                         setEnrollment(enrollmentRes.data);
                         setIsEnrolled(true);
                     }
                } catch (enrollError) {
                    if (enrollError.response && enrollError.response.status !== 404) {
                         console.error("Enrollment status error:", enrollError);
                    }
                }
            }
        } catch (err) {
            console.error("Error fetching course:", err);
            if (err.response?.status === 404) {
                setCourse(null);
            } else {
                toast.error("Course information could not be loaded.");
            }
        } finally {
            setLoading(false);
        }
    }, [id, token, initialData]);

    useEffect(() => {
        fetchCourseAndEnrollment();
    }, [fetchCourseAndEnrollment]);

    const handleEnroll = async () => {
        if (!user) return navigate('/login');
        const loadingToast = toast.loading('Enrolling in course...');
        try {
            const config = { headers: { 'x-auth-token': token } };
            await axios.post(`${API_BASE_URL}/api/enrollments/enroll/${id}`, {}, config);
            toast.success('Successfully enrolled in the course!', { id: loadingToast });
            fetchCourseAndEnrollment();
        } catch (err) {
            toast.error(err.response?.data?.msg || "An error occurred during enrollment.", { id: loadingToast });
        }
    };

    if (loading) return <Spinner />;
    if (!course) return <div className="text-center card p-8">Course not found or an error occurred while loading.</div>;

    const totalPages = course.readingPages?.length || 0;
    const completedPagesCount = enrollment?.completedPages?.length || 0;
    const allPagesCompleted = isEnrolled && totalPages > 0 && completedPagesCount === totalPages;
    const isCourseCompleted = enrollment?.completed || enrollment?.certificateEarned;
    
    // Progress hesaplama: %80 okuma + %20 quiz
    let progressPercentage = 0;
    if (isCourseCompleted) {
        progressPercentage = 100;
    } else if (isEnrolled) {
        // Okuma progress'i (max %80)
        let readingProgress = 0;
        if (totalPages > 0) {
            readingProgress = (completedPagesCount / totalPages) * 80;
        }
        
        // Quiz progress'i (max %20)
        let quizProgress = 0;
        if (course.quizId && enrollment?.quizScore !== undefined && enrollment?.quizScore !== null) {
            quizProgress = 20;
        }
        
        progressPercentage = Math.round(readingProgress + quizProgress);
    }

    const nextUnreadPage = isEnrolled ? (enrollment?.completedPages?.length || 0) + 1 : 1;
    const startReadingLink = isCourseCompleted 
                            ? `/profile` // Tamamlanmış kurs için profile yönlendir
                            : totalPages === 0
                                ? (course.quizId ? `/quiz/${course.quizId}` : `/courses/${id}`)
                                : (allPagesCompleted && course.quizId
                                    ? `/quiz/${course.quizId}`
                                    : `/courses/${id}/read/${nextUnreadPage > totalPages ? totalPages : nextUnreadPage}`);

    return (
        <>
            <SEO 
                title={`${course.title} | AircoreDynamics`}
                description={course.description}
                keywords={course.tags?.join(', ') || 'aviation course, aerospace course, online learning'}
                image={course.imageUrl}
                url={`/courses/${course.slug || course._id}`}
                type="course"
                course={{
                    title: course.title,
                    description: course.description,
                    level: course.level,
                    duration: course.duration || 'Self-paced',
                    rating: course.rating || (4.0 + Math.random() * 1.0).toFixed(1), // Random 4.0-5.0
                    reviewCount: course.reviewCount || Math.floor(15 + Math.random() * 85) // Random 15-100
                }}
                breadcrumb={[
                    { name: 'Home', url: '/' },
                    { name: 'Courses', url: '/courses' },
                    { name: course.title, url: `/courses/${course.slug || course._id}` }
                ]}
            />
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="container mx-auto px-4 py-6 max-w-7xl">
            {/* Hero Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                {/* Left: Course Image & Info */}
                <div className="lg:col-span-2 space-y-4">
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="card overflow-hidden"
                    >
                        <div className="relative w-full aspect-video overflow-hidden bg-gradient-to-br from-blue-500 to-purple-600">
                            <img 
                                src={course.imageUrl || 'https://via.placeholder.com/1200x600.png?text=Aircoredynamics'} 
                                alt={`${course.title} - ${course.difficulty} level aviation course`}
                                className="w-full h-full object-cover object-center"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                        </div>
                        
                        <div className="p-6">
                            <div className="flex items-center gap-2 mb-3">
                                <Link 
                                    to={`/courses?category=${course.category}`}
                                    className="text-xs font-semibold px-3 py-1 rounded-full bg-light-primary/10 dark:bg-dark-primary/10 text-light-primary dark:text-dark-primary hover:bg-light-primary/20 dark:hover:bg-dark-primary/20 transition-colors"
                                >
                                    {course.category || 'Genel'}
                                </Link>
                            </div>
                            
                            <h1 className="text-2xl md:text-3xl font-bold mb-4 leading-tight">{course.title}</h1>
                            
                            <div className="flex items-start justify-between gap-4 mb-4">
                                <p className="text-base leading-relaxed opacity-80 flex-1">{course.description}</p>
                                <ShareButton 
                                    url={`https://aircoredynamics.com/courses/${course.slug || course._id}`}
                                    title={course.title}
                                    description={course.description}
                                />
                            </div>

                            {/* Tags */}
                            {course.tags && course.tags.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {course.tags.map(tag => (
                                        <Link
                                            key={tag}
                                            to={`/courses?tag=${tag}`}
                                            className="text-xs px-3 py-1 rounded-full bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border hover:border-light-primary dark:hover:border-dark-primary transition-colors"
                                        >
                                            #{tag}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    </motion.div>

                    {/* Progress Card */}
                    {isEnrolled && totalPages > 0 && (
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="card p-5"
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <ChartBarIcon className="h-5 w-5 text-light-primary dark:text-dark-primary" />
                                    <span className="font-semibold text-sm">Course Progress</span>
                                </div>
                                <span className="text-xl font-bold text-light-primary dark:text-dark-primary">
                                    {progressPercentage.toFixed(0)}%
                                </span>
                            </div>
                            <div className="w-full bg-light-bg dark:bg-dark-bg rounded-full h-2.5 overflow-hidden mb-2">
                                <motion.div 
                                    className="bg-gradient-to-r from-light-primary to-blue-500 dark:from-dark-primary dark:to-cyan-400 h-2.5"
                                    initial={{ width: 0 }}
                                    animate={{ width: `${progressPercentage}%` }}
                                    transition={{ duration: 1, ease: "easeOut" }}
                                />
                            </div>
                            <div className="space-y-1">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="opacity-70">Reading Pages</span>
                                    <span className="font-semibold">{completedPagesCount}/{totalPages} (%{Math.round((completedPagesCount/totalPages)*80)})</span>
                                </div>
                                {course.quizId && (
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="opacity-70">Quiz</span>
                                        <span className="font-semibold">
                                            {enrollment?.quizScore !== undefined && enrollment?.quizScore !== null ? '✓ Completed (%20)' : 'Not Started (%0)'}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}
                </div>

                {/* Right: Course Stats & CTA */}
                <div className="space-y-4">
                    <motion.div 
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="card p-6 sticky top-20"
                    >
                        <h3 className="font-bold text-lg mb-4">Course Details</h3>
                        
                        <div className="space-y-4 mb-6">
                            <div className="flex items-center gap-3 text-sm">
                                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-light-primary/10 dark:bg-dark-primary/10 flex items-center justify-center">
                                    <AcademicCapIcon className="h-5 w-5 text-light-primary dark:text-dark-primary" />
                                </div>
                                <div>
                                    <p className="text-xs opacity-60">Difficulty</p>
                                    <p className="font-semibold">{course.difficulty || 'Not Specified'}</p>
                                </div>
                            </div>

                            {course.estimatedTime && (
                                <div className="flex items-center gap-3 text-sm">
                                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-light-primary/10 dark:bg-dark-primary/10 flex items-center justify-center">
                                        <ClockIcon className="h-5 w-5 text-light-primary dark:text-dark-primary" />
                                    </div>
                                    <div>
                                        <p className="text-xs opacity-60">Duration</p>
                                        <p className="font-semibold">{course.estimatedTime}</p>
                                    </div>
                                </div>
                            )}

                            <div className="flex items-center gap-3 text-sm">
                                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-light-primary/10 dark:bg-dark-primary/10 flex items-center justify-center">
                                    <BookOpenIcon className="h-5 w-5 text-light-primary dark:text-dark-primary" />
                                </div>
                                <div>
                                    <p className="text-xs opacity-60">Content</p>
                                    <p className="font-semibold">{totalPages} Pages</p>
                                </div>
                            </div>
                            
                            {course.enrollmentCount > 0 && (
                                <div className="flex items-center gap-3 text-sm">
                                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-light-primary/10 dark:bg-dark-primary/10 flex items-center justify-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5 text-light-primary dark:text-dark-primary">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-xs opacity-60">Enrolled</p>
                                        <p className="font-semibold">{course.enrollmentCount.toLocaleString()} Students</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="border-t border-light-border dark:border-dark-border pt-4">
                            {!user ? (
                                <Link to="/login" className="btn-primary w-full flex items-center justify-center gap-2 !py-2.5 !text-sm font-semibold">
                                    <PlayIcon className="h-4 w-4" />
                                    Login to Start
                                </Link>
                            ) : isEnrolled ? (
                                isCourseCompleted ? (
                                    <div className="space-y-3">
                                        <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-lg p-4 text-center">
                                            <CheckCircleIcon className="h-8 w-8 text-green-600 dark:text-green-400 mx-auto mb-2" />
                                            <p className="text-sm font-semibold text-green-700 dark:text-green-300">
                                                Course Completed
                                            </p>
                                            <p className="text-xs opacity-70 mt-1">
                                                You've earned your certificate!
                                            </p>
                                        </div>
                                        {totalPages > 0 && (
                                            <Link 
                                                to={`/courses/${id}/read/1`} 
                                                className="btn-secondary w-full flex items-center justify-center gap-2 !py-2.5 !text-sm font-semibold"
                                            >
                                                <BookOpenIcon className="h-4 w-4" />
                                                Review Content
                                            </Link>
                                        )}
                                        <Link to="/profile" className="btn-secondary w-full flex items-center justify-center gap-2 !py-2.5 !text-sm font-semibold">
                                            <AcademicCapIcon className="h-4 w-4" />
                                            View Profile
                                        </Link>
                                    </div>
                                ) : (
                                    <Link to={startReadingLink} className="btn-primary w-full flex items-center justify-center gap-2 !py-2.5 !text-sm font-semibold">
                                        {totalPages === 0 ? (
                                            course.quizId ? (
                                                <>
                                                    <TrophyIcon className="h-4 w-4" />
                                                    Start Quiz
                                                </>
                                            ) : "Enrolled"
                                        ) : allPagesCompleted ? (
                                            course.quizId ? (
                                                <>
                                                    <TrophyIcon className="h-4 w-4" />
                                                    Final Quiz
                                                </>
                                            ) : "Review Course"
                                        ) : (
                                            <>
                                                <ArrowRightIcon className="h-4 w-4" />
                                                {completedPagesCount > 0 ? `Continue (${nextUnreadPage}/${totalPages})` : "Start Course"}
                                            </>
                                        )}
                                    </Link>
                                )
                            ) : (
                                <button onClick={handleEnroll} className="btn-primary w-full flex items-center justify-center gap-2 !py-2.5 !text-sm font-semibold">
                                    <BookOpenIcon className="h-4 w-4" />
                                    Enroll Now
                                </button>
                            )}
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Course Content List */}
            {user && isEnrolled && totalPages > 0 && (
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="card p-6"
                >
                    <div className="flex items-center justify-between mb-5">
                        <h2 className="text-xl font-bold">Course Content</h2>
                        <span className="text-xs bg-light-bg dark:bg-dark-bg px-3 py-1.5 rounded-full font-semibold">
                            {completedPagesCount} / {totalPages}
                        </span>
                    </div>
                    
                    <div className="space-y-2">
                        {course.readingPages.sort((a, b) => a.pageNumber - b.pageNumber).map((page, idx) => {
                            const isCompleted = enrollment?.completedPages?.includes(page.pageNumber);
                            const isCurrent = !isCompleted && (enrollment?.completedPages?.length || 0) === page.pageNumber - 1;
                            
                            return (
                                <motion.div
                                    key={page.pageNumber}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: idx * 0.03 }}
                                    className={`group relative flex items-center justify-between p-3 rounded-lg border transition-all ${
                                        isCompleted 
                                            ? 'border-green-500/20 bg-green-500/5 hover:bg-green-500/10' 
                                            : isCurrent
                                                ? 'border-light-primary/30 dark:border-dark-primary/30 bg-light-primary/5 dark:bg-dark-primary/5'
                                                : 'border-light-border dark:border-dark-border hover:border-light-primary/30 dark:hover:border-dark-primary/30'
                                    }`}
                                >
                                    {isCurrent && (
                                        <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-1 h-6 bg-light-primary dark:bg-dark-primary rounded-r" />
                                    )}
                                    
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${
                                            isCompleted 
                                                ? 'bg-green-500 text-white' 
                                                : isCurrent 
                                                    ? 'bg-light-primary dark:bg-dark-primary text-white'
                                                    : 'bg-light-bg dark:bg-dark-bg'
                                        }`}>
                                            {isCompleted ? <CheckCircleIcon className="h-5 w-5" /> : page.pageNumber}
                                        </div>
                                        
                                        <div className="min-w-0 flex-1">
                                            <p className={`font-medium text-sm truncate ${isCompleted ? 'text-green-600 dark:text-green-400' : ''}`}>
                                                {page.title || `Chapter ${page.pageNumber}`}
                                            </p>
                                            {isCurrent && <p className="text-xs text-light-primary dark:text-dark-primary font-medium">In Progress</p>}
                                        </div>
                                    </div>
                                    
                                    <Link 
                                        to={`/courses/${id}/read/${page.pageNumber}`} 
                                        className={`btn-secondary !py-1.5 !px-3 text-xs flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ${
                                            isCurrent ? '!opacity-100' : ''
                                        }`}
                                    >
                                        {isCompleted ? "Review" : isCurrent ? "Continue" : "Start"}
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Quiz Section */}
                    {allPagesCompleted && course.quizId && !isCourseCompleted && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.3 }}
                            className="mt-4 p-4 rounded-lg bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30"
                        >
                            <div className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                                        <TrophyIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-sm">Final Quiz Ready</h3>
                                        <p className="text-xs opacity-70">Complete to earn certificate</p>
                                    </div>
                                </div>
                                <Link to={`/quiz/${course.quizId}`} className="btn-primary text-xs !py-2 !px-4 whitespace-nowrap">
                                    Start Quiz
                                </Link>
                            </div>
                        </motion.div>
                    )}
                    
                    {/* Already Completed Quiz Message */}
                    {allPagesCompleted && course.quizId && isCourseCompleted && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.3 }}
                            className="mt-4 p-4 rounded-lg bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800"
                        >
                            <div className="flex items-center gap-3">
                                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                                    <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-sm text-green-700 dark:text-green-300">Quiz Completed</h3>
                                    <p className="text-xs opacity-70">You've already earned your certificate for this course</p>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </motion.div>
            )}

            {user && isEnrolled && totalPages === 0 && (
                <div className="card p-6 text-center opacity-70">
                    <BookOpenIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No reading content has been added for this course yet.</p>
                </div>
            )}
        </motion.div>
        </>
    );
};

export default CourseDetailPage;
              